import React from 'react'
import { cn } from '@/lib/utils'
import { Progress } from './Progress'

type AsyncContentProgressProps = {
  loading: boolean
  label?: string
  className?: string
  children: React.ReactNode
}

/**
 * Componente genérico para exibir um texto + barra de progresso
 * enquanto dados assíncronos são carregados. Ao finalizar,
 * completa a barra até 100% e em seguida exibe o conteúdo.
 */
export function AsyncContentProgress({
  loading,
  label = 'Aguarde, calculando dados...',
  className,
  children,
}: AsyncContentProgressProps) {
  const [progress, setProgress] = React.useState(0)
  const [showContent, setShowContent] = React.useState(false)

  React.useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    if (loading) {
      setShowContent(false)
      setProgress(0)

      // Simula avanço gradual até ~95% enquanto carrega
      intervalId = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 95) return prev
          return prev + 5
        })
      }, 120)
    } else {
      // Completa a barra rapidamente até 100% e, em seguida,
      // libera a renderização do conteúdo
      setProgress(100)
      timeoutId = setTimeout(() => {
        setShowContent(true)
      }, 150)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [loading])

  if (showContent && !loading) {
    return <>{children}</>
  }

  // Se não estiver carregando e ainda não há conteúdo para mostrar, não renderiza nada
  if (!loading && !children) {
    return null
  }

  return (
    <div className={cn('flex flex-col gap-1 text-xs sm:text-sm text-foreground/80', className)}>
      {label && <span>{label}</span>}
      <Progress value={progress} />
    </div>
  )
}

